import { useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Button, Field, Row, Segmented, Sheet } from '../ui'
import { useLists } from '../lib/queries'
import { KindIcon } from './KindIcon'
import type { List, ListItem, ListKind } from '../lib/types'

/**
 * Tasks plan T1.2 (D5–D8): the one sheet a task opens — title, note, due,
 * repeat, and *Move to…* as a second view inside it. The lists brief's Rule 1
 * (everything inline except the due date) is this sheet grown to four
 * fields; the row's inline title edit stays for the common case.
 *
 * The sheet sets fields; it does not parse words. "by Friday" and "every
 * week" are the add field's (`listAdd.ts`'s `parseTask`), never read here.
 */
export interface TaskPatch {
  body?: string
  note?: string | null
  due_on?: string | null
  repeat_every?: number | null
  repeat_unit?: 'day' | 'week' | null
}

type RepeatChoice = 'never' | 'daily' | 'weekly' | 'every'

function choiceOf(every: number | null, unit: 'day' | 'week' | null): RepeatChoice {
  if (!every || !unit) return 'never'
  if (every === 1) return unit === 'day' ? 'daily' : 'weekly'
  return 'every'
}

export function TaskSheet({ item, list, onClose, onSave, onMove }: {
  item: ListItem
  list: List
  onClose: () => void
  /** Only the fields that changed; an empty patch is no write. */
  onSave: (patch: TaskPatch) => void
  onMove: (to: List) => void
}) {
  const [view, setView] = useState<'edit' | 'move'>('edit')
  const [body, setBody] = useState(item.body)
  const [note, setNote] = useState(item.note ?? '')
  const [due, setDue] = useState(item.due_on ?? '')
  const every0 = item.repeat_every ?? null
  const unit0 = item.repeat_unit ?? null
  const [choice, setChoice] = useState<RepeatChoice>(choiceOf(every0, unit0))
  const [every, setEvery] = useState(String(every0 && every0 > 1 ? every0 : 2))
  const [unit, setUnit] = useState<'day' | 'week'>(unit0 ?? 'week')

  const listsQ = useLists()
  const others = (listsQ.data ?? []).filter(l => l.id !== list.id && !l.archived_at)

  function repeatOf(): { repeat_every: number | null; repeat_unit: 'day' | 'week' | null } {
    if (choice === 'never') return { repeat_every: null, repeat_unit: null }
    if (choice === 'daily') return { repeat_every: 1, repeat_unit: 'day' }
    if (choice === 'weekly') return { repeat_every: 1, repeat_unit: 'week' }
    const n = Math.min(365, Math.max(1, Math.round(Number(every) || 1)))
    return { repeat_every: n, repeat_unit: unit }
  }

  function save() {
    const patch: TaskPatch = {}
    const b = body.trim()
    if (b && b !== item.body) patch.body = b
    const n = note.trim() || null
    if (n !== (item.note ?? null)) patch.note = n
    const d = due || null
    if (d !== (item.due_on ?? null)) patch.due_on = d
    const r = repeatOf()
    if (r.repeat_every !== every0 || r.repeat_unit !== unit0) { patch.repeat_every = r.repeat_every; patch.repeat_unit = r.repeat_unit }
    onSave(patch)
  }

  if (view === 'move') {
    return (
      <Sheet open onOpenChange={o => { if (!o) onClose() }} title={`Move "${item.body}"`} className="tasksheet"
        actions={<Button variant="secondary" onClick={() => setView('edit')}><ChevronLeft size={18} aria-hidden="true" /> Back</Button>}>
        {others.length ? others.map(l => (
          <Row key={l.id} className="u-row--list" leading={<KindIcon kind={l.kind as ListKind} size={20} />}
            title={l.name} meta={null} onPress={() => onMove(l)} />
        )) : <p className="stub">No other list to move it to.</p>}
      </Sheet>
    )
  }

  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={item.body} className="tasksheet"
      actions={
        <>
          <Button variant="primary" onClick={save}>Save</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </>
      }>
      <Field label="Title">
        <Field.Input value={body} onChange={e => setBody(e.target.value)} autoComplete="off" />
      </Field>
      <Field label="Note">
        <Field.Textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="A line or two" />
      </Field>
      <Field label="Due">
        <span className="tasksheet__due">
          <Field.Input type="date" value={due} onChange={e => setDue(e.target.value)} />
          {due && <Button variant="secondary" size="sm" onClick={() => setDue('')}>None</Button>}
        </span>
      </Field>
      <Field label="Repeat">
        <Segmented
          value={choice} onChange={v => setChoice(v as RepeatChoice)}
          options={[
            { value: 'never', label: 'Never' }, { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' }, { value: 'every', label: 'Every…' },
          ]}
        />
      </Field>
      {choice === 'every' && (
        <div className="tasksheet__every">
          <Field.Input type="number" inputMode="numeric" min={1} max={365} value={every} aria-label="Every"
            onChange={e => setEvery(e.target.value)} className="tasksheet__everyn" />
          <Field.Select value={unit} aria-label="Unit" onChange={v => setUnit(v as 'day' | 'week')}
            options={[{ value: 'day', label: 'days' }, { value: 'week', label: 'weeks' }]} />
        </div>
      )}
      <Row className="u-row--list tasksheet__move" leading={null} title="Move to…" meta={null} chevron="right"
        onPress={() => setView('move')} />
    </Sheet>
  )
}
